/**
 * MainLayout.tsx
 * =============================================================================
 * Global layout component that wraps all routes.
 * Maintains persistent timer logic and displays MiniPlayer when needed.
 *
 * CRITICAL: Timer tick logic lives here to survive route changes.
 *
 * @author Neuro-Audio Lab
 * @version 1.0.0
 */

import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { usePlayerStore } from '../../store/playerStore';
import { MiniPlayer } from '../player/MiniPlayer';

export function MainLayout() {
  const location = useLocation();

  // Timer state from store
  const timerState = usePlayerStore((s) => s.timerState);
  const remainingTime = usePlayerStore((s) => s.remainingTime);
  const tickSessionTime = usePlayerStore((s) => s.tickSessionTime);
  const tickRemainingTime = usePlayerStore((s) => s.tickRemainingTime);

  // Audio state
  const isPlaying = usePlayerStore((s) => s.isPlaying);
  const isPaused = usePlayerStore((s) => s.isPaused);
  const currentMode = usePlayerStore((s) => s.currentMode);

  // Actions needed to stop audio and timer when the countdown reaches zero
  const stop = usePlayerStore((s) => s.stop);
  const setTimerState = usePlayerStore((s) => s.setTimerState);

  // Ref to hold interval (number in browser, not NodeJS.Timeout)
  const intervalRef = useRef<number | null>(null);

  // =========================================================================
  // CRITICAL: GLOBAL TIMER TICK LOGIC
  // This runs at the layout level, so it survives route changes
  // =========================================================================
  useEffect(() => {
    const isActive = timerState === 'running' || timerState === 'break';

    if (isActive) {
      // Start interval: tick every second
      intervalRef.current = setInterval(() => {
        // Always tick session time (counts up)
        tickSessionTime();

        // Tick remaining time (counts down) if > 0
        if (remainingTime > 0) {
          tickRemainingTime();
        }
      }, 1000);

      console.log('[MainLayout] Timer interval started');
    } else {
      // Clear interval when not active
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        console.log('[MainLayout] Timer interval cleared');
      }
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [timerState, remainingTime, tickSessionTime, tickRemainingTime]);

  // =========================================================================
  // TIMER END DETECTION
  // When the countdown reaches exactly 0 while the timer was actively running
  // and audio is still playing, trigger a natural stop with fade-out and
  // put the timer back to idle so the interval clears itself.
  // Dependency array is intentionally minimal to prevent double-firing:
  // we only care when remainingTime changes to 0, not on every render.
  // =========================================================================
  useEffect(() => {
    // Guard: only act when the countdown just hit zero during an active session
    if (remainingTime !== 0) return;
    if (timerState !== 'running') return;

    console.log('[MainLayout] Timer reached zero — stopping audio and resetting timer');

    // 1. Stop audio engine (triggers natural fade-out via playerStore.stop())
    stop();

    // 2. Put timer back to idle so the setInterval clears itself (see effect above)
    setTimerState('idle');
  }, [remainingTime, timerState, stop, setTimerState]);

  // =========================================================================
  // MINI PLAYER VISIBILITY LOGIC
  // Show MiniPlayer when:
  // - NOT on home page (/), AND
  // - (Audio is playing OR timer is running OR session has progress)
  // =========================================================================
  const isHomePage = location.pathname === '/';
  const hasActiveSession = isPlaying || isPaused || timerState !== 'idle';
  const shouldShowMiniPlayer = !isHomePage && hasActiveSession && currentMode;

  return (
    <>
      {/* Route Content */}
      <Outlet />

      {/* Mini Player - Floats when not on home */}
      {shouldShowMiniPlayer && <MiniPlayer />}
    </>
  );
}

export default MainLayout;
